


import { Component, Prop, Vue } from "vue-property-decorator";

import { Newsletters } from "App";
import { CustomNotification } from "@Internal/Models";
import { $t, EventBus } from "App/IOC";
import { NewsletterBlog } from "App/Models/Newsletter";

import DatePicker from "Web/Components/Common/Input/DatePicker.vue";
import Icon from "Web/Components/Icon.vue";

import BlogPreview from "./BlogPreview.vue";

import { FormatDate } from "./Helpers";

/* Un article tel que le rend `console/group/{id}/blogs/{blogId}/posts`. */
interface BlogPostRow {
    id: number;
    title: string;
    url: string;
    picture?: string | null;
    status: string;
    published_at?: string | null;
    newsletter?: { exclude?: boolean; targets?: { [ key: string ]: unknown } };
    /* Demandes au back le 26/10/2026 : affiches des qu'ils arrivent. */
    author?: string | null;
    description?: string | null;
    tags?: { id: number; name: string; color?: string | null }[];
}

/* Ce que l'equipe modifie d'un blog, par blog. */
interface BlogDraft {
    name: string;
    urlFeed: string;
    startDate: string;
}

/* LES BLOGS DU CLIENT, EN LECTURE ( Fred, 26/10/2026 : « est-ce qu'on peut remonter les
   parametres du blog du client dans la Console ? » ).

   Ce que l'API Console lit aujourd'hui : chaque blog ( nom, flux, statut, derniere
   lecture du flux ) et ses articles ( date, statut, reglage newsletter ). Rien ne s'y
   modifie : les routes d'ecriture ( nom, flux, date d'import, « Mettre a jour » ) et
   `cta_script_url` sont demandes au back.

   Meme ecran que les parametres d'un blog au Desktop ( Fred, 26/10/2026 : « je dois
   avoir la meme chose dans la Console » ) : informations generales, suivi du blog et
   apercu sur trois colonnes, puis l'extrait du flux. */
@Component({
    components: {
        BlogPreview,
        DatePicker,
        Icon
    }
})
export default class NewsletterGroupBlogs extends Vue {
    @Prop( { required: true } )
    readonly GroupId!: number;

    private blogs: NewsletterBlog[] = [];
    private loading: boolean = true;

    /* Les articles d'un blog deplie, par id ; `null` = en cours de lecture. */
    private posts: { [ id: string ]: BlogPostRow[] | null } = {};
    private open: number[] = [];

    /* Le blog ouvert ; `0` = la liste des blogs, d'abord, comme au Desktop ( Fred,
       26/10/2026 ). */
    private selected: number = 0;

    /* PRECODE ( Fred, 26/10/2026 : « comme ca, quand la route sera livree, on aura rien a
       faire » ) : les champs se modifient et « Mettre a jour » appelle les routes demandees
       au back ( `…/blogs/{id}/update` puis `…/refresh` ). Tant qu'elles n'existent pas,
       l'API repond 404 sans `blog_not_found` : on le dit, et le bouton se grise. */
    private drafts: { [ id: string ]: BlogDraft } = {};
    private saving: number[] = [];
    private writeUnavailable: boolean = false;
    private copied: number = 0;

    /* CREER UN BLOG POUR LE CLIENT ( Fred, 26/10/2026 : « il faut aussi pouvoir creer un
       blog pour le client depuis la Console » ). Memes champs qu'au Desktop ( Blogs RSS →
       Ajouter ), date d'import a aujourd'hui par defaut. Le back le cree au nom du
       proprietaire du compte. */
    private creating: boolean = false;
    private createBusy: boolean = false;
    private createDraft: BlogDraft = { name: "", urlFeed: "", startDate: "" };

    /* Le blog qui vient d'etre cree : ses articles arrivent en tache de fond, on relit la
       liste quelques fois tant qu'elle est vide ( comme au Desktop ). */
    private importing: number = 0;
    private importTimer: number = 0;

    async mounted(): Promise<void> {
        const [ blogs, error ] = await Newsletters.Blogs( this.GroupId );

        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.blogs = blogs || [];
        this.resetDrafts();

    }

    beforeDestroy(): void {
        window.clearTimeout( this.importTimer );
    }

    private today(): string {
        const now = new Date();
        const pad = ( value: number ): string => ( value < 10 ? "0" : "" ) + value;

        return `${ now.getFullYear() }-${ pad( now.getMonth() + 1 ) }-${ pad( now.getDate() ) }`;
    }

    private startCreate(): void {
        this.createDraft = { name: "", urlFeed: "", startDate: this.today() };
        this.creating = true;
    }

    private get canCreate(): boolean {
        const draft = this.createDraft;

        return !this.createBusy && draft.name.trim() !== "" && draft.urlFeed.trim() !== ""
            && draft.startDate !== "";
    }

    private async create(): Promise<void> {
        if( !this.canCreate )
            return;

        this.createBusy = true;

        const [ blog, error ] = await Newsletters.BlogCreate( this.GroupId, {
            name: this.createDraft.name.trim(),
            urlFeed: this.createDraft.urlFeed.trim(),
            startDate: this.createDraft.startDate.replace( /-/g, "/" )
        } );

        this.createBusy = false;

        if( error !== undefined ) {
            if( this.routeMissing( error ) )
                this.writeUnavailable = true;
            else
                EventBus.Publish( "Error", error );

            return;
        }

        this.creating = false;

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "NEWSLETTERS_blogsTitle" ), $t( "NEWSLETTERS_blogCreated" )
        ) );

        /* Ouvrir le blog cree, puis suivre l'import de ses articles. */
        const id = ( blog !== undefined && blog !== null ) ? Number( blog.id ) : 0;

        this.selected = id;
        await this.reload();

        if( id > 0 ) {
            this.importing = id;
            this.pollImport( id, 0 );
        }
    }

    private pollImport( id: number, round: number ): void {
        window.clearTimeout( this.importTimer );

        this.importTimer = window.setTimeout( async () => {
            if( this.importing !== id || this.selected !== id )
                return;

            const [ rows ] = await Newsletters.BlogPosts( this.GroupId, id, 100 );
            const found = ( rows || [] ) as BlogPostRow[];

            if( found.length > 0 || round >= 9 ) {
                this.importing = 0;
                this.$set( this.posts, String( id ), found );
                await this.reload();
                return;
            }

            this.pollImport( id, round + 1 );
        }, 3000 );
    }

    private resetDrafts(): void {
        this.blogs.forEach( blog => this.$set(
            this.drafts, String( blog.id ), this.draftOf( blog )
        ) );
    }

    private draftOf( blog: NewsletterBlog ): BlogDraft {
        return {
            name: blog.name || "",
            urlFeed: blog.url_feed || "",
            startDate: ( blog.start_date || "" ).slice( 0, 10 )
        };
    }

    private draft( blog: NewsletterBlog ): BlogDraft {
        return this.drafts[ String( blog.id ) ] || this.draftOf( blog );
    }

    private isSaving( blog: NewsletterBlog ): boolean {
        return this.saving.indexOf( blog.id ) >= 0;
    }

    /* « Valider l'URL » pour un blog jamais lu, « Mettre a jour » ensuite — comme au
       Desktop. */
    private refreshLabel( blog: NewsletterBlog ): string {
        return $t( blog.imported_at ? "NEWSLETTERS_blogUpdate" : "NEWSLETTERS_blogValidateUrl" );
    }

    /* La route manque : 404 de l'API ( route a venir ), ou refus du relais de la Console
       tant que son back n'est pas redeploye ( `route_not_allowed` ). Un vrai 404 du
       blog ( `blog_not_found` ) reste une erreur. */
    private routeMissing( error: { Id?: unknown; Message?: unknown; SubId?: unknown } ): boolean {
        const text = `${ String( error.Message || "" ) } ${ String( error.SubId || "" ) }`;

        if( text.indexOf( "blog_not_found" ) >= 0 )
            return false;

        return Number( error.Id ) === 404 || /not found|route_not_allowed/i.test( text );
    }

    private async save( blog: NewsletterBlog ): Promise<void> {
        if( this.isSaving( blog ) || this.writeUnavailable )
            return;

        const draft = this.draft( blog );
        const original = this.draftOf( blog );
        const fields: { name?: string; urlFeed?: string; startDate?: string } = {};

        if( draft.name.trim() !== original.name )
            fields.name = draft.name.trim();

        if( draft.urlFeed.trim() !== original.urlFeed )
            fields.urlFeed = draft.urlFeed.trim();

        if( draft.startDate !== original.startDate && draft.startDate !== "" )
            fields.startDate = draft.startDate.replace( /-/g, "/" );

        this.saving.push( blog.id );

        if( Object.keys( fields ).length > 0 ) {
            const [ , updateError ] = await Newsletters.BlogUpdate( this.GroupId, blog.id, fields );

            if( updateError !== undefined ) {
                this.fail( updateError );
                return;
            }
        }

        const [ , refreshError ] = await Newsletters.BlogRefresh( this.GroupId, blog.id );

        if( refreshError !== undefined ) {
            this.fail( refreshError );
            return;
        }

        this.saving = this.saving.filter( id => id !== blog.id );

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "NEWSLETTERS_blogsTitle" ), $t( "NEWSLETTERS_blogUpdated" )
        ) );

        /* Le flux se relit en tache de fond : la liste se recharge, les articles suivent. */
        await this.reload();
    }

    /* Publier / Archiver le blog : son `status` ( `active` / `inactive` ), par la meme route
       de modification ( reponse back du 26/10/2026, § 3 ). */
    private async toggleStatus( blog: NewsletterBlog ): Promise<void> {
        if( this.isSaving( blog ) || this.writeUnavailable )
            return;

        this.saving.push( blog.id );

        const status = this.active( blog.status ) ? "inactive" : "active";
        const [ , error ] = await Newsletters.BlogUpdate( this.GroupId, blog.id, { status } );

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.saving = this.saving.filter( id => id !== blog.id );

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "NEWSLETTERS_blogsTitle" ),
            $t( status === "active" ? "NEWSLETTERS_blogPublished" : "NEWSLETTERS_blogArchived" )
        ) );

        await this.reload();
    }

    /* SUPPRIMER UN BLOG : DEUX GARDE-FOUS ( Fred, 26/10/2026 : « supprimer, oui, ca supprime
       tout : c'est pour ca qu'il faut deux garde-fous » ). Comme au Desktop
       ( `Blogs/ConfirmDelete.ts` ) : d'abord ce qui part, nommement ( le blog et le nombre
       de ses articles ), puis le dernier mot. La seconde s'ouvre au tour suivant. */
    private async confirmDelete( blog: NewsletterBlog ): Promise<void> {
        if( this.isSaving( blog ) || this.writeUnavailable )
            return;

        const [ count ] = await Newsletters.BlogPostsCount( this.GroupId, blog.id );
        const message = ( typeof count === "number" && !isNaN( count ) )
            ? $t( "NEWSLETTERS_blogDeleteStep1", { name: blog.name || "", count } )
            : $t( "NEWSLETTERS_blogDeleteStep1All", { name: blog.name || "" } );

        EventBus.Publish( "Confirmation", {
            Props: {
                Message: message,
                Actions: [ {
                    Id: "continue",
                    Name: $t( "NEWSLETTERS_blogDeleteContinue" ),
                    Active: true
                } ]
            },
            Events: {
                Action: (): void => {
                    window.setTimeout( () => EventBus.Publish( "Confirmation", {
                        Props: {
                            Message: $t( "NEWSLETTERS_blogDeleteStep2", { name: blog.name || "" } ),
                            Actions: [ {
                                Id: "delete",
                                Name: $t( "NEWSLETTERS_blogDeleteFinal" ),
                                Active: true
                            } ]
                        },
                        Events: { Action: () => this.remove( blog ) }
                    } ), 0 );
                }
            }
        } );
    }

    private async remove( blog: NewsletterBlog ): Promise<void> {
        this.saving.push( blog.id );

        const [ , error ] = await Newsletters.BlogDelete( this.GroupId, blog.id );

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.saving = this.saving.filter( id => id !== blog.id );

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "NEWSLETTERS_blogsTitle" ), $t( "NEWSLETTERS_blogDeleted" )
        ) );

        this.selected = 0;
        await this.reload();
    }

    private fail( error: any ): void {
        this.saving = [];

        if( this.routeMissing( error ) ) {
            this.writeUnavailable = true;
            return;
        }

        EventBus.Publish( "Error", error );
    }

    private async reload(): Promise<void> {
        const [ blogs ] = await Newsletters.Blogs( this.GroupId );

        if( blogs === undefined )
            return;

        this.blogs = blogs;
        this.resetDrafts();
        this.posts = {};
        this.open = [];

        const current = this.blogs.find( blog => blog.id === this.selected );

        if( current !== undefined )
            this.toggle( current );
    }

    /* Le code a coller, des que l'API rend `cta_script_url`. */
    private snippet( blog: NewsletterBlog ): string {
        const close = "</" + "script>";

        return blog.cta_script_url
            ? `<script async src="${ blog.cta_script_url }">${ close }`
            : "";
    }

    private copyLabel( blog: NewsletterBlog ): string {
        return $t( this.copied === blog.id ? "NEWSLETTERS_blogCopied" : "NEWSLETTERS_blogCopy" );
    }

    private copy( blog: NewsletterBlog ): void {
        const text = this.snippet( blog );

        if( text === "" )
            return;

        navigator.clipboard.writeText( text );
        this.copied = blog.id;

        window.setTimeout( () => {
            if( this.copied === blog.id )
                this.copied = 0;
        }, 2000 );
    }

    /* Auteur et extrait : des colonnes seulement quand l'API les rend. */
    private hasField( blog: NewsletterBlog, field: "author" | "description" ): boolean {
        return ( this.postsOf( blog ) || [] ).some( post => !!post[ field ] );
    }

    private get shownBlogs(): NewsletterBlog[] {
        return this.blogs.filter( blog => blog.id === this.selected );
    }

    private select( blog: NewsletterBlog ): void {
        this.selected = blog.id;

        if( !this.isOpen( blog ) )
            this.toggle( blog );
    }

    private isOpen( blog: NewsletterBlog ): boolean {
        return this.open.indexOf( blog.id ) >= 0;
    }

    private async toggle( blog: NewsletterBlog ): Promise<void> {
        const index = this.open.indexOf( blog.id );

        if( index >= 0 ) {
            this.open.splice( index, 1 );
            return;
        }

        this.open.push( blog.id );

        if( this.posts[ String( blog.id ) ] !== undefined )
            return;

        this.$set( this.posts, String( blog.id ), null );

        const [ rows, error ] = await Newsletters.BlogPosts( this.GroupId, blog.id, 100 );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            this.$set( this.posts, String( blog.id ), [] );
            return;
        }

        this.$set( this.posts, String( blog.id ), ( rows || [] ) as BlogPostRow[] );
    }

    private postsOf( blog: NewsletterBlog ): BlogPostRow[] | null {
        const rows = this.posts[ String( blog.id ) ];

        return ( rows === undefined ) ? null : rows;
    }

    private active( status: unknown ): boolean {
        return String( status || "active" ) === "active";
    }

    private date( value: string | null | undefined, withTime: boolean = false ): string {
        return FormatDate( value, withTime );
    }

    private targeted( post: BlogPostRow ): boolean {
        const targets = ( post.newsletter !== undefined ) ? post.newsletter.targets : undefined;

        return targets !== undefined && targets !== null && Object.keys( targets ).length > 0;
    }
}
